import DOMPurify from 'dompurify';

// Older widgets still write HTML strings and assign plain strings to script.src.
// Sanitize/validate those writes instead of installing a pass-through policy that
// would defeat Trusted Types enforcement.
export function initializeTrustedTypes(): void {
    const trustedTypes = (globalThis as any).trustedTypes;
    if (!trustedTypes) return;

    trustedTypes.createPolicy('default', {
        // The browser stringifies the returned TrustedHTML; 'dompurify' is allowed in trusted-types.
        createHTML: (html: string) => DOMPurify.sanitize(html, { RETURN_TRUSTED_TYPE: true }),
        createScriptURL: validateScriptUrl,
        // No createScript: executable strings must be replaced with normal code.
    });
}

export function validateScriptUrl(value: string): string {
    const url = new URL(value, document.baseURI);
    if (url.username || url.password) throw new TypeError('Script URL credentials are forbidden');

    // ngsw-worker.js is registered via navigator.serviceWorker.register(), also a script URL sink.
    if (url.origin === location.origin && /^(?:\/assets\/[^?#]+\.m?js|\/ngsw-worker\.js)$/.test(url.pathname)) {
        return url.href;
    }

    const paths: Record<string, RegExp> = {
        'https://www.googletagmanager.com': /^\/gtag\/js$/,
        'https://www.google-analytics.com': /^\/(?:analytics|ga)\.js$/,
        'https://maps.googleapis.com': /^(?:\/maps\/api\/js(?:\/.*)?|\/maps-api-v3\/.*\.js)$/,
        'https://maps.gstatic.com': /^\/maps-api-v3\/.*\.js$/,
        'https://www.google.com': /^\/recaptcha\/(?:api|enterprise)\.js$/,
        'https://www.gstatic.com': /^\/recaptcha\/releases\/[^/]+\/recaptcha__[a-zA-Z_-]+\.js$/,
        // CdnBaseUrl is az-cdn.selise.biz, or cdn.selise.biz on dev-az.
        'https://az-cdn.selise.biz': /^\/(?:selisecdn\/cdn\/)?common\/[^?#]+\.js$/,
        'https://cdn.selise.biz': /^\/common\/[^?#]+\.js$/,
        'https://cdn.mxpnl.com': /^\/libs\/mixpanel-[^/]+\.min\.js$/,
        'https://static.hotjar.com': /^\/c\/hotjar-\d+\.js$/,
        'https://cdnjs.cloudflare.com': /^\/ajax\/libs\/[^?#]+\.m?js$/,
    };
    if (paths[url.origin]?.test(url.pathname)) return url.href;
    throw new TypeError(`Unapproved script URL: ${url.origin}${url.pathname}`);
}
